import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { MyJoinRequest, fetchMyJoinRequest } from '../../services/joinRequestService';
import { createLogger } from '../../utils/logger';

const logger = createLogger('useMyJoinRequest');

/**
 * The signed-in user's own join request, if they have one.
 *
 * Cached per user id at module level for the same reason `AuthContext` caches: this is
 * read by the app-wide guard AND by the trial banner, so an uncached hook would fire two
 * requests on every navigation for the overwhelming majority of users — who have no join
 * request at all and never will. One request per session, shared.
 *
 * Deliberately not folded into `AuthContext`: this is only ever non-empty for someone who
 * signed up into a domain-matched org, and threading it through the auth cache would make
 * every login pay for a lookup that almost always comes back empty.
 */
const cache = new Map<string, Promise<MyJoinRequest>>();

/** A user who has never made a request. Frozen and shared so its identity is stable
 *  across renders and consumers can safely put it in dependency arrays. */
const NONE: MyJoinRequest = Object.freeze({ can_re_request: false });

export function invalidateMyJoinRequest(userId?: string): void {
  if (userId) cache.delete(userId);
  else cache.clear();
}

export interface UseMyJoinRequest {
  request: MyJoinRequest;
  /** True until the first response lands. The guard must not decide anything while this
   *  is set, or it would flash the product at a blocked applicant. */
  loading: boolean;
  refresh: () => void;
}

export function useMyJoinRequest(): UseMyJoinRequest {
  const { currentUser } = useAuth();
  const userId = currentUser?.id;

  const [request, setRequest] = useState<MyJoinRequest>(NONE);
  const [loading, setLoading] = useState<boolean>(!!userId);
  // Bumped by refresh() to re-run the effect after the cache entry is dropped.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!userId) {
      setRequest(NONE);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    let pending = cache.get(userId);
    if (!pending) {
      pending = fetchMyJoinRequest();
      // Never cache a failure: a transient 500 must not pin every later read to an empty
      // result, which would silently un-block a pending applicant for the rest of their
      // session.
      pending.catch(() => cache.delete(userId));
      cache.set(userId, pending);
    }

    pending
      .then(result => {
        if (!cancelled) setRequest(result ?? NONE);
      })
      .catch(error => {
        // Failing open is right here. The backend enforces the same block on the roleplay
        // entry point, so a network blip cannot actually let a pending applicant practise —
        // whereas failing closed would wall out every ordinary user whenever this one
        // endpoint hiccups.
        logger.warn('Could not load join request status; treating as none', error);
        if (!cancelled) setRequest(NONE);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, reloadToken]);

  const refresh = useCallback(() => {
    if (userId) cache.delete(userId);
    setReloadToken(token => token + 1);
  }, [userId]);

  return { request, loading, refresh };
}
